/**
 * usePersistedFilterState - useState that mirrors its value into localStorage so
 * a signed-in user's Filter Control selections survive navigation and reloads.
 *
 * The app ships sensible defaults for the list filters (status defaults to
 * In Progress / Pending, the date range looks back 2 weeks). While a user is
 * signed in, any change they make to those filters is written to localStorage
 * under the `gbrands_filter_pref:` namespace so it persists across page loads.
 * On sign-out (manual logout or session expiry) `clearPersistedFilters()` wipes
 * that namespace, so the next session starts fresh from the app defaults — this
 * is what scopes the persistence to "until they sign out".
 *
 * The list pages are auth-gated (ProtectedRoute), so by the time this hook runs
 * the user is already signed in. Tying the lifetime to the sign-out wipe lets us
 * keep the hook a drop-in useState replacement without threading auth state
 * through every call site.
 */
import { useState, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';

/** Namespace prefix for every persisted Filter Control preference key. */
const FILTER_PREF_PREFIX = 'gbrands_filter_pref:';

/** Build the namespaced localStorage key for a given preference name. */
const prefKey = (name: string): string => `${FILTER_PREF_PREFIX}${name}`;

/**
 * Drop-in replacement for useState whose value is persisted to localStorage.
 *
 * @param name          Stable, page-scoped preference name (e.g. "work-orders.statuses").
 * @param defaultValue  Fallback used when nothing is persisted yet.
 * @param overrideValue One-shot initial value that wins over BOTH the persisted
 *                      copy and the default — used by deep links (e.g. the
 *                      dashboard stat cards passing ?status=… to a list page).
 *                      Only read on mount; pass undefined for normal behavior.
 *                      The override is written through to localStorage by the
 *                      mirror effect below, so it behaves exactly as if the
 *                      user had picked it in the Filter Control.
 */
export function usePersistedFilterState<T>(
	name: string,
	defaultValue: T,
	overrideValue?: T,
): [T, Dispatch<SetStateAction<T>>] {
	const key = prefKey(name);

	// Lazy initializer: a deep-link override wins outright; otherwise hydrate
	// from localStorage when a stored copy exists, else fall back to the
	// caller-supplied default.
	const [value, setValue] = useState<T>(() => {
		if (overrideValue !== undefined) return overrideValue;
		try {
			const raw = localStorage.getItem(key);
			if (raw != null) return JSON.parse(raw) as T;
		} catch {
			/* Corrupt JSON or no storage access — fall through to the default. */
		}
		return defaultValue;
	});

	// Mirror every change back into localStorage so the selection persists
	// across reloads/navigations for as long as the user stays signed in.
	useEffect(() => {
		try {
			localStorage.setItem(key, JSON.stringify(value));
		} catch {
			/* Quota / private-mode failures are non-fatal for a filter pref. */
		}
	}, [key, value]);

	return [value, setValue];
}

/**
 * Removes every persisted Filter Control preference. Called from the auth
 * teardown (logout + session expiry) so a signed-out user — and the next user
 * to sign in on the same device — starts from the app defaults rather than
 * inheriting the previous session's saved filters.
 */
export function clearPersistedFilters(): void {
	try {
		Object.keys(localStorage)
			.filter((k) => k.startsWith(FILTER_PREF_PREFIX))
			.forEach((k) => localStorage.removeItem(k));
	} catch {
		/* No storage access — nothing to clear. */
	}
}
